import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Library, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/**
 * Quick picker for pulling sale comps from the landlord's persistent Comp
 * Library into a hypothetical-or-real valuation. Returns an array of
 * `{address, sale_price, square_footage, sale_date, notes}` shaped to match
 * the `ValuationManualComp` Pydantic model on the backend.
 */
export default function CompLibraryPicker({ open, onOpenChange, propertyType, onPick }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const params = { comp_type: "sale", limit: 200 };
      if (propertyType) params.property_type = propertyType;
      const r = await api.get("/comps", { params });
      setItems(r.data.items || []);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load comps");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, propertyType]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const apply = () => {
    const picked = items.filter((c) => selected.has(c.id));
    if (!picked.length) {
      toast.error("Select at least one comp");
      return;
    }
    const mapped = picked
      .filter((c) => c.address && c.sale_price && c.sqft)
      .map((c) => ({
        address: c.formatted_address || c.address,
        sale_price: c.sale_price,
        square_footage: c.sqft,
        sale_date: c.sale_date || null,
        notes: c.notes || `From library · ${c.market || ""}${c.submarket ? " · " + c.submarket : ""}`.trim(),
      }));
    if (mapped.length === 0) {
      toast.error("Selected comps are missing address / price / sqft");
      return;
    }
    onPick(mapped);
    toast.success(`${mapped.length} comp${mapped.length === 1 ? "" : "s"} pulled from library`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="comp-library-picker">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Library className="w-5 h-5 text-slate-500" /> Pull from your comp library
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-slate-500 mb-3">
          Showing sale comps{propertyType ? ` for ${propertyType.replace(/_/g, " ")}` : ""} ordered most-recent first. Pick any to inject into this valuation.
        </div>
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2 overline text-slate-600">Address</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">Sqft</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">Sale price</th>
                <th className="px-3 py-2 overline text-slate-600 text-right">$/sqft</th>
                <th className="px-3 py-2 overline text-slate-600">Date</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="text-center py-8 text-slate-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-400">No sale comps in your library yet — go to the Comps page to add one.</td></tr>
              )}
              {items.map((c) => {
                const checked = selected.has(c.id);
                return (
                  <tr
                    key={c.id}
                    onClick={() => toggle(c.id)}
                    className={`border-b border-slate-100 last:border-0 cursor-pointer ${checked ? "bg-emerald-50/40" : "hover:bg-slate-50"}`}
                    data-testid={`picker-comp-${c.id}`}
                  >
                    <td className="px-3 py-2">
                      <input type="checkbox" readOnly checked={checked} />
                    </td>
                    <td className="px-3 py-2 font-semibold text-slate-900 max-w-xs truncate" title={c.formatted_address || c.address}>{c.formatted_address || c.address}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.sqft ? c.sqft.toLocaleString() : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono font-bold">{c.sale_price ? `$${c.sale_price.toLocaleString()}` : "—"}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.price_per_sqft ? `$${c.price_per_sqft}` : "—"}</td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-500">{c.sale_date || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter className="mt-3">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-outline-dark">Cancel</button>
          <button type="button" onClick={apply} className="btn-primary-dark inline-flex items-center gap-2" data-testid="picker-apply-btn">
            <CheckCircle2 className="w-4 h-4" /> Pull {selected.size || ""}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
